import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DmsConfiguration, IServerApi, VehicleType} from '@service-and-repairs/awpintegrationlib';
import {ReplaySubject} from 'rxjs';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {EtkConfiguration} from '../interfaces/etk-configuration';
import {LeadsConfiguration} from '../interfaces/leads-configuration';
import {OutletRelatedConfiguration} from '../interfaces/outlet-related-configuration';
import {ConfigurationService} from './configuration.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationLoader {
  private readonly configurationService = inject(ConfigurationService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);

  configChanged: ReplaySubject<{
    outletRelatedConfig: OutletRelatedConfiguration,
    etkConfig: EtkConfiguration
  }> = new ReplaySubject<{
    outletRelatedConfig: OutletRelatedConfiguration,
    etkConfig: EtkConfiguration
  }>(1);
  private outletRelatedConfiguration: OutletRelatedConfiguration;

  private readonly awpLib: IServerApi;
  private businessPartnerId: string;
  private vehicleType: VehicleType;
  private user: UserData;

  constructor() {
    const awpClientLibService = inject(AwpClientLibService);
    const userService = this.userService;

    this.awpLib = awpClientLibService.serverApi;
    userService.user$.subscribe((user: UserData): void => {
      this.user = user;
      this.reloadConfig(false);
    });
  }

  showFlatRateUnitValues(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.flatrateValuesAllowed
      || this.hasUserAccessToFlatRateUnits(
        this.user,
        this.outletRelatedConfiguration?.outletConfiguration?.usersWithFlatrateValueAccess
      );
  }

  isFlatRateUnitAmountZeroAllowed(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.isFlatRateUnitAmountZeroAllowed;
  }

  isServiceHistoryDeletionAllowed(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.serviceHistoryEntryDeletionAllowed;
  }

  isGdprEnabled(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.airGdprReportAllowed;
  }

  isDcomConfigured(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.dcomUrl?.trim().length > 0;
  }

  isDmsWriteAccessAllowed(): boolean {
    return this.user
      && this.outletRelatedConfiguration?.outletConfiguration?.dmsWriteAccessAllowed;
  }

  isDmsLicensePlateSearchAllowed(): boolean {
    return this.user
      && this.outletRelatedConfiguration?.outletConfiguration?.airNumberPlateSearchAllowed;
  }

  getSpecialistGroups(): string[] {
    return this.outletRelatedConfiguration?.outletConfiguration?.specialistGroups ?? [];
  }

  showFullRetailSaving(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.showFullRetailSaving ?? false;
  }

  getLeadsDealerConfig(): LeadsConfiguration {
    return this.outletRelatedConfiguration?.leadsDealerConfiguration;
  }

  getDmsConfiguration(): DmsConfiguration {
    return {
      // Integrated apps query prices via DCOM only, so this stays the DCOM price query flag
      isPriceAndStockRetrievalEnabled: this.isDcomConfigured(),
      isAutomaticPriceAndStockRetrievalEnabled: this.outletRelatedConfiguration?.outletConfiguration?.retrievePricesAndAvailabilitiesAutomatically
        ?? false,
      isRetailPriceRetrievalEnabled: this.outletRelatedConfiguration?.outletConfiguration?.showFullRetailSaving
        ?? false,
      isAwpDmsInterfaceEnabled: this.outletRelatedConfiguration?.outletConfiguration?.awpDmsInterfaceEnabled ?? false
    };
  }

  isAppointmentSchedulingEnabled(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.appointmentSchedulingInterfaceEnabled ?? false;
  }

  isDmsOrderRequiredForAppointmentSchedulingSystem(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.dmsOrderRequiredForAppointmentSchedulingSystem
      ?? false;
  }

  isSoftPlanningEnabled(): boolean {
    return this.outletRelatedConfiguration?.outletConfiguration?.softPlanningEnabled ?? false;
  }

  private hasUserAccessToFlatRateUnits(user: UserData, users: string[]): boolean {
    return users?.some((name: string): boolean => name.toLowerCase() === user.getName().toLowerCase())
      || users?.some((loginName: string): boolean => loginName.toLowerCase() === user.getLoginName().toLowerCase());
  }

  reloadConfig(force: boolean): void {
    if ((force || this.mustReloadConfig()) && this.canReloadConfig()) {
      this.businessPartnerId = this.user?.getBusinessPartner()?.getBusinessPartnerId();
      this.vehicleType = this.user.getSelectedVehicleType();
      Promise.all([this.getOutletConfig(), this.getEtkConfig()])
        .then(([outletRelatedConfig, etkConfig]): void => this.onConfigChanged(
          outletRelatedConfig,
          etkConfig
        ));
    }
  }

  private onConfigChanged(outletRelatedConfig: OutletRelatedConfiguration, etkConfig: EtkConfiguration): void {
    if (outletRelatedConfig || etkConfig) {
      this.outletRelatedConfiguration = outletRelatedConfig;
      this.configChanged.next({
        outletRelatedConfig,
        etkConfig
      });
      this.awpLib.publishOutletConfiguration({
        dmsConfiguration: this.getDmsConfiguration()
      });
    }
  }

  private mustReloadConfig(): boolean {
    return this.user?.getBusinessPartner()?.getBusinessPartnerId() !== this.businessPartnerId
      || this.user?.getSelectedVehicleType() !== this.vehicleType;
  }

  private canReloadConfig(): boolean {
    return this.user?.getBusinessPartner()?.getBusinessPartnerId() != undefined;
  }

  private getOutletConfig(): Promise<OutletRelatedConfiguration> {
    return new Promise(resolve => {
      this.configurationService.getOutletConfigFromBackend(this.businessPartnerId, this.vehicleType)
        .then((outletRelatedConfiguration: OutletRelatedConfiguration): void => resolve(outletRelatedConfiguration))
        .catch((): void => {
          this.showErrorMessageOnceTranslationExists('config.loadError');
          resolve(undefined);
        });
    });
  }

  private getEtkConfig(): Promise<EtkConfiguration> {
    if (!this.user?.getRoles().includes('awp_b2d_etk')) {
      return Promise.resolve(undefined);
    }

    return new Promise(resolve => {
      this.configurationService.getEtkConfigFromBackend(
          this.user.getBusinessPartner().getBusinessPartnerId(),
          this.user.getSelectedVehicleType()
        )
        .then((etkConfig: EtkConfiguration) => resolve(etkConfig))
        .catch((): void => {
          this.showErrorMessageOnceTranslationExists('config.loadErrorETK');
          resolve(undefined);
        });
    });
  }

  private showErrorMessageOnceTranslationExists(key: string): void {
    this.translate.get(key).subscribe((translation: string) => this.awpLib.notifyError(translation));
  }
}
